// Check Doc: scans the workbook and reports anomalies in the course full names.
//
// Read-only with respect to the file. It never writes anything, which is why it takes
// no file handle and never asks for write permission - that absence is the guarantee.

import {
  cohortHint,
  eightSegments,
  headingCohorts,
  isValidCohort,
  isValidSession,
  nameKey,
  normalise,
  parseValue,
  sessionHint,
  tildeSpacing,
  tutorName,
} from "./courseNameRules";
import { collectCourseRows } from "./courseRows";
import { findClosestModuleName, getModuleIndex } from "./moduleMappings";

/**
 * Which report section a finding belongs to.
 *
 * Carried as a field rather than recovered by matching the message text. Grouping the
 * duplicate rule by group means those lines start "Rows 23, 27, 31:" instead of
 * "Row 23: Segment 8 ...", so prefix matching would drop every duplicate line into
 * OTHER. OTHER still exists, for any finding that arrives without a known section.
 */
export const SECTION = {
  STRUCTURE: "structure",
  SPACING: "spacing",
  CODE: "seg1",
  NAME: "seg4",
  COHORT: "seg5",
  COHORT_MISMATCH: "cohortMismatch",
  TUTOR: "seg7",
  TUTOR_AMBIGUOUS: "tutorAmbiguous",
  SESSION: "seg8",
  DUPLICATE: "duplicates",
  OTHER: "other",
};

/**
 * Scan the sheet and collect every anomaly.
 *
 * @param {Array<Array>} rows - sheet as an array of row arrays
 * @param {{startRow: number, index: object}} options
 */
export const checkDocument = (rows, options = {}) => {
  const index = options.index || getModuleIndex();
  const { courseRows, headingCount, scanned } = collectCourseRows(rows, options.startRow);

  const findings = [];
  const duplicateGroups = [];
  const missingReference = [];
  const buckets = {};
  // CD-9: rows whose heading names no month and year, by heading, so none vanish unchecked
  const cohortNotCompared = {};
  const cohortsByHeading = new Map();

  // CD-6 skips rows CD-2 already reported, so those row numbers are tracked as we go.
  const reportedByCode = new Set();
  // Which rows used which code for which module name, for CD-6.
  const codeUsage = {};
  // CD-11: column E tutors by lower-cased surname, then by forenames, for the whole sheet,
  // and the rows of each column E name whose surname is more than one word.
  const tutorsBySurname = new Map();
  const multiWordTutors = new Map();

  const add = (rowNumber, section, text) => findings.push({ rowNumber, section, text });

  courseRows.forEach((entry) => {
    const { rowNumber } = entry;

    // CD-11 - recorded before CD-1 can return. It is about column E alone, so a broken
    // course name does not take its tutor out of the comparison.
    const tutor = tutorName(entry.tutor);
    if (tutor.surname) {
      const surnameKey = tutor.surname.toLowerCase();
      if (!tutorsBySurname.has(surnameKey)) {
        tutorsBySurname.set(surnameKey, { surname: tutor.surname, byForenames: new Map() });
      }
      const byForenames = tutorsBySurname.get(surnameKey).byForenames;
      if (!byForenames.has(tutor.forenames)) {
        byForenames.set(tutor.forenames, { name: entry.tutor, rowNumbers: [] });
      }
      byForenames.get(tutor.forenames).rowNumbers.push(rowNumber);
    }
    if (tutor.multiWord) {
      if (!multiWordTutors.has(entry.tutor)) multiWordTutors.set(entry.tutor, []);
      multiWordTutors.get(entry.tutor).push(rowNumber);
    }

    // Only B is read and fixed when both columns carry a name, so a stale C would
    // silently disagree with it. Say so rather than leaving it unexplained.
    if (entry.bothColumns) {
      add(rowNumber, SECTION.OTHER, "both B and C hold a tilde name; only B was checked.");
    }

    // CD-1 - segment count. Nothing else can be trusted if this fails.
    const untrimmed = eightSegments(entry.text);
    if (!untrimmed) {
      add(
        rowNumber,
        SECTION.STRUCTURE,
        `Expected 8 tilde segments, found ${countSegments(entry.text)}`
      );
      return;
    }

    // CD-8 - blank space next to a tilde. Read from the raw cell, which still has the
    // spaces at either end. Every later rule checks the trimmed segments, so " Apr26" is
    // reported once, here, and not a second time as a malformed cohort.
    const spacing = tildeSpacing(entry.raw);
    if (spacing.length > 0) {
      add(rowNumber, SECTION.SPACING, `Spaces next to "~": ${spacing.map(describeSpacing).join("; ")}`);
    }
    const segments = untrimmed.map((segment) => segment.trim());

    const code = String(segments[0]).toUpperCase();
    const moduleName = segments[3];
    const cohort = segments[4];
    const tutorSurname = segments[6];
    const session = segments[7];

    const key = nameKey(moduleName);
    const expectedCode = index.nameKeyToCode[key];
    const knownModule = index.modules[code];
    const knownName = knownModule && knownModule.name;

    // CD-2 - the module name is known, so the code is checkable.
    if (expectedCode) {
      if (expectedCode !== code) {
        reportedByCode.add(rowNumber);
        add(
          rowNumber,
          SECTION.CODE,
          `Segment 1 for module code, Expected: ${expectedCode}, Found: ${segments[0]}, Course : ${moduleName}.`
        );
      }
      if (!codeUsage[key]) codeUsage[key] = [];
      codeUsage[key].push({ rowNumber, code });
    } else if (knownName) {
      // CD-3 - the name is unknown but the code is known, so the name is checkable.
      add(
        rowNumber,
        SECTION.NAME,
        `Segment 4 for module name, Expected: ${knownName}, Found: ${moduleName}.`
      );
    } else {
      // CD-3 - neither is known. Offer a spelling suggestion only when it is credible.
      const closest = findClosestModuleName(moduleName, index);
      if (closest) {
        add(
          rowNumber,
          SECTION.NAME,
          `Segment 4 probable spelling mistake, Found: ${moduleName}, Probable: ${closest.name}.`
        );
      } else {
        missingReference.push(rowNumber);
      }
    }

    // CD-4 - cohort format.
    if (!isValidCohort(cohort)) {
      add(
        rowNumber,
        SECTION.COHORT,
        `Segment 5 for cohort, Expected Format: ${cohortHint(cohort)}, Found: "${cohort}"`
      );
    }

    // CD-9 - the cohort must be the month and year its category heading names. A cohort
    // that cannot be read is already reported by CD-4, so only a readable one is compared.
    if (!cohortsByHeading.has(entry.categoryLabel)) {
      cohortsByHeading.set(entry.categoryLabel, entry.category ? headingCohorts(entry.category) : []);
    }
    const expectedCohorts = cohortsByHeading.get(entry.categoryLabel);
    const parsedCohort = parseValue(cohort);

    if (expectedCohorts.length === 0) {
      if (!cohortNotCompared[entry.categoryLabel]) cohortNotCompared[entry.categoryLabel] = [];
      cohortNotCompared[entry.categoryLabel].push(rowNumber);
    } else if (parsedCohort && expectedCohorts.indexOf(parsedCohort.month + parsedCohort.year) === -1) {
      add(
        rowNumber,
        SECTION.COHORT_MISMATCH,
        `Segment 5 cohort does not match its category, Expected: ${expectedCohorts.join(" or ")}, ` +
          `Found: ${cohort}, Category: ${entry.categoryLabel}`
      );
    }

    // CD-5 - session format.
    if (!isValidSession(session)) {
      add(
        rowNumber,
        SECTION.SESSION,
        `Segment 8 for session, Expected Format: ${sessionHint(session)}, Found: "${session}"`
      );
    }

    // CD-10 - segment 7 must be the column E surname, case and all. A blank on either side
    // is a mismatch too, even when both are blank: every course row needs a tutor.
    if (!tutorSurname || tutorSurname !== tutor.surname) {
      add(
        rowNumber,
        SECTION.TUTOR,
        `Segment 7 for tutor, Found: ${tutorSurname || "(empty)"}, Column E: ${entry.tutor || "(empty)"}.`
      );
    }

    // CD-7 - bucket for the duplicate-group rule.
    //
    // Keyed on the module NAME, not the code: a wrong code is validated separately and
    // must not split one course into two buckets. Keyed on the WHOLE segment 8, not just
    // the group letter, because "F26A" and "Apr26A" are different sessions, not a clash.
    // Segment 8 is not required to be well-formed - a duplicate is still a duplicate
    // when the format is also wrong. The category is scoped by its department: Business
    // and Health and Social Care both run "February 2026 Cohort Foundation Year - B3",
    // and the same course and group in each is two classes, not one duplicated.
    const categoryKey = `${normalise(entry.department)}|${normalise(entry.categoryLabel)}`;
    const moduleKey = normalise(moduleName) || code;
    const groupKey = String(session).toUpperCase();

    if (!buckets[categoryKey]) {
      buckets[categoryKey] = { label: entry.categoryLabel, department: entry.department, modules: {} };
    }
    const modules = buckets[categoryKey].modules;
    if (!modules[moduleKey]) modules[moduleKey] = { label: moduleName || segments[0], groups: {} };
    const groups = modules[moduleKey].groups;
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(rowNumber);
  });

  // CD-6 - one module name mapped to two codes in the reference data itself.
  index.conflicts.forEach((conflict) => {
    const key = nameKey(conflict.moduleName);
    (codeUsage[key] || []).forEach((use) => {
      if (use.code !== conflict.codeB) return;
      if (reportedByCode.has(use.rowNumber)) return;
      add(
        use.rowNumber,
        SECTION.CODE,
        `Segment 1 for module code, Expected: ${conflict.codeA}, Found: ${conflict.codeB}, Course : ${conflict.moduleName}.`
      );
    });
  });

  // CD-7 - emit one line per duplicated group rather than per repeated row. Every
  // repeated row still counts toward the row total, so the summary stays honest.
  const duplicateRowNumbers = new Set();
  Object.keys(buckets).forEach((categoryKey) => {
    const bucket = buckets[categoryKey];
    Object.keys(bucket.modules).forEach((moduleKey) => {
      const module = bucket.modules[moduleKey];
      Object.keys(module.groups).forEach((groupKey) => {
        const rowNumbers = module.groups[groupKey];
        if (rowNumbers.length < 2) return;

        const repeated = rowNumbers.slice(1);
        repeated.forEach((rowNumber) => duplicateRowNumbers.add(rowNumber));

        duplicateGroups.push({
          section: SECTION.DUPLICATE,
          rowNumbers,
          repeated,
          firstRow: rowNumbers[0],
          group: groupKey,
          module: module.label,
          category: bucket.label,
          department: bucket.department,
          text:
            `Rows ${repeated.join(", ")}: Segment 8 duplicate group "${groupKey}" for ` +
            `${module.label} under "${bucket.label}"` +
            `${bucket.department ? ` (${bucket.department})` : ""}, first used on row ${rowNumbers[0]} ` +
            `(${rowNumbers.length} rows: ${rowNumbers.join(", ")}).`,
        });
      });
    });
  });

  // CD-11 - one line per surname that column E gives more than one set of forenames,
  // "Tariq" with none counting as different from "M. Tariq". Segment 7 holds only the
  // surname, so it cannot say which of them a row means. Every row is listed for review.
  const tutorGroups = [];
  tutorsBySurname.forEach(({ surname, byForenames }) => {
    if (byForenames.size < 2) return;

    const variants = Array.from(byForenames.values());
    const rowNumbers = variants
      .reduce((all, variant) => all.concat(variant.rowNumbers), [])
      .sort((a, b) => a - b);

    tutorGroups.push({
      kind: "sharedSurname",
      section: SECTION.TUTOR_AMBIGUOUS,
      rowNumbers,
      surname,
      variants,
      text:
        `Rows ${rowNumbers.join(", ")}: surname "${surname}" in column E belongs to more than ` +
        `one tutor: ` +
        variants
          .map(
            (variant) =>
              `${variant.name} (row${variant.rowNumbers.length === 1 ? "" : "s"} ` +
              `${variant.rowNumbers.join(", ")})`
          )
          .join("; ") +
        ".",
    });
  });

  // CD-11 - one line per column E name with more than one word before the surname, such as
  // "D. Di Emidio". A surname must be one word, so the name cannot say whether segment 7
  // should read "Emidio" or "DiEmidio". A full first name ("Enayet Sarwar") lands here too,
  // because it cannot be told apart from a two-word surname.
  multiWordTutors.forEach((rowNumbers, name) => {
    tutorGroups.push({
      kind: "multiWord",
      section: SECTION.TUTOR_AMBIGUOUS,
      rowNumbers,
      name,
      text:
        `Row${rowNumbers.length === 1 ? "" : "s"} ${rowNumbers.join(", ")}: column E "${name}" ` +
        `has more than one word after the initials, so its surname is unclear. ` +
        `Write the surname as one word.`,
    });
  });

  const rowsTouched = new Set();
  findings.forEach((finding) => rowsTouched.add(finding.rowNumber));
  duplicateRowNumbers.forEach((rowNumber) => rowsTouched.add(rowNumber));
  tutorGroups.forEach((group) => group.rowNumbers.forEach((rowNumber) => rowsTouched.add(rowNumber)));

  return {
    findings,
    duplicateGroups,
    tutorGroups,
    missingReference,
    cohortNotCompared,
    headingCount,
    scanned,
    courseRowCount: courseRows.length,
    counts: {
      issues: findings.length + duplicateGroups.length + tutorGroups.length,
      rows: rowsTouched.size,
    },
  };
};

/** One piece of a CD-8 finding: 'segment 4 "Effective Communication  " (2 trailing)'. */
const describeSpacing = ({ piece, value, leading, trailing }) => {
  if (piece === 9) return `after the last ~ (${leading} space${leading === 1 ? "" : "s"})`;
  if (value.trim() === "") return `segment ${piece} "${value}" (only spaces)`;
  const counts = [];
  if (leading) counts.push(`${leading} leading`);
  if (trailing) counts.push(`${trailing} trailing`);
  return `segment ${piece} "${value}" (${counts.join(", ")})`;
};

/** How many segments a name really has, for the CD-1 message. */
const countSegments = (value) => {
  const parts = String(value).split("~");
  while (parts.length > 0 && parts[parts.length - 1].trim() === "") parts.pop();
  return parts.length;
};
